import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChildren,
  QueryList,
  ElementRef,
  OnInit,
  OnDestroy,
} from '@angular/core';

@Component({
  selector: 'app-otp-input',
  templateUrl: './otp-input.component.html',
  styleUrls: ['./otp-input.component.sass'],
})
export class OtpInputComponent implements OnInit, OnDestroy {
  @Input() length: number = 6;
  @Input() disabled: boolean = false;
  @Input() autoSubmit: boolean = true;
  @Input() hasError: boolean = false;

  @Output() codeComplete = new EventEmitter<string>();
  @Output() codeChange = new EventEmitter<string>();

  @ViewChildren('otpInput') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  digits: string[] = [];
  private shakeTimeout: any;

  ngOnInit(): void {
    this.digits = Array(this.length).fill('');
  }

  ngOnDestroy(): void {
    if (this.shakeTimeout) {
      clearTimeout(this.shakeTimeout);
    }
  }

  /**
   * Maneja el input de cada dígito
   */
  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Solo permitir un dígito numérico
    if (value && !/^\d$/.test(value)) {
      input.value = '';
      return;
    }

    this.digits[index] = value;
    this.emitCodeChange();

    // Auto-avanzar al siguiente input
    if (value && index < this.length - 1) {
      this.focusInput(index + 1);
    }

    // Auto-submit cuando está completo
    if (this.autoSubmit && this.isComplete()) {
      this.codeComplete.emit(this.getCode());
    }
  }

  /**
   * Maneja teclas especiales (backspace, flechas)
   */
  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;

    switch (event.key) {
      case 'Backspace':
        if (!input.value && index > 0) {
          // Si está vacío y presiona backspace, ir al anterior
          event.preventDefault();
          this.focusInput(index - 1);
          this.digits[index - 1] = '';
          this.emitCodeChange();
        } else if (input.value) {
          // Si tiene valor, limpiarlo
          this.digits[index] = '';
          this.emitCodeChange();
        }
        break;

      case 'ArrowLeft':
        if (index > 0) {
          event.preventDefault();
          this.focusInput(index - 1);
        }
        break;

      case 'ArrowRight':
        if (index < this.length - 1) {
          event.preventDefault();
          this.focusInput(index + 1);
        }
        break;

      case 'Delete':
        this.digits[index] = '';
        input.value = '';
        this.emitCodeChange();
        break;
    }
  }

  /**
   * Maneja el pegado de código completo
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text')?.trim() || '';

    // Extraer solo números
    const numbers = pastedData.replace(/\D/g, '');

    if (numbers.length === 0) return;

    // Llenar los inputs con los números pegados
    const chars = numbers.slice(0, this.length).split('');
    chars.forEach((char, i) => {
      this.digits[i] = char;
      const inputsArray = this.inputs.toArray();
      if (inputsArray[i]) {
        inputsArray[i].nativeElement.value = char;
      }
    });

    this.emitCodeChange();

    // Focus en el último dígito llenado o el siguiente vacío
    const nextIndex = Math.min(chars.length, this.length - 1);
    this.focusInput(nextIndex);

    // Auto-submit si está completo
    if (this.autoSubmit && this.isComplete()) {
      setTimeout(() => {
        this.codeComplete.emit(this.getCode());
      }, 50);
    }
  }

  /**
   * Previene entrada de caracteres no numéricos
   */
  onKeyPress(event: KeyboardEvent): void {
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Focus en un input específico
   */
  private focusInput(index: number): void {
    const inputsArray = this.inputs?.toArray();
    if (inputsArray && inputsArray[index]) {
      inputsArray[index].nativeElement.focus();
      inputsArray[index].nativeElement.select();
    }
  }

  /**
   * Emite el código actual
   */
  private emitCodeChange(): void {
    this.codeChange.emit(this.getCode());
  }

  /**
   * Obtiene el código completo
   */
  getCode(): string {
    return this.digits.join('');
  }

  /**
   * Verifica si el código está completo
   */
  isComplete(): boolean {
    return this.digits.every((d) => d !== '') && this.digits.length === this.length;
  }

  /**
   * Limpia todos los inputs (público)
   */
  reset(): void {
    this.digits = Array(this.length).fill('');
    const inputsArray = this.inputs?.toArray();
    if (inputsArray) {
      inputsArray.forEach((input) => {
        input.nativeElement.value = '';
      });
    }
    this.focusInput(0);
  }

  /**
   * Focus en el primer input (público)
   */
  focus(): void {
    this.focusInput(0);
  }

  /**
   * Activa animación de error (shake)
   */
  triggerError(): void {
    this.hasError = true;
    if (this.shakeTimeout) {
      clearTimeout(this.shakeTimeout);
    }
    this.shakeTimeout = setTimeout(() => {
      this.hasError = false;
    }, 500);
  }

  /**
   * TrackBy para ngFor
   */
  trackByIndex(index: number): number {
    return index;
  }
}
